/**
 * Allen & Heath dLive: channel model, value scales and store paths, shared by
 * the bridge (MIDI over TCP) and the UI.
 *
 * Everything here follows the "dLive MIDI Over TCP/IP Protocol, firmware V2.0"
 * spec and was checked against a DM0 MixRack (OB1, firmware V2.x). See
 * docs/dlive-midi.md for the protocol notes and the measured quirks.
 */
// ─── Channel types ───────────────────────────────────────────
export const DLIVE_CHANNEL_TYPES = [
    "input",
    "monoGroup",
    "stereoGroup",
    "monoAux",
    "stereoAux",
    "monoMatrix",
    "stereoMatrix",
    "monoFxSend",
    "stereoFxSend",
    "fxReturn",
    "main",
    "dca",
    "muteGroup",
    "ufxSend",
    "ufxReturn",
];
const NO_SENDS = [];
const STRIP = {
    stereo: false,
    name: true,
    colour: true,
    fader: true,
    mute: true,
    mainAssign: false,
    hpf: false,
    peq: false,
    dcaAssign: true,
    sends: NO_SENDS,
};
const INPUT_SENDS = [
    { kind: "group", level: false, on: true },
    { kind: "aux", level: true, on: true },
    { kind: "fxSend", level: true, on: false },
    { kind: "ufxSend", level: true, on: false },
    { kind: "matrix", level: true, on: false },
];
const FX_RETURN_SENDS = [
    { kind: "aux", level: true, on: false },
    { kind: "fxSend", level: true, on: false },
    { kind: "ufxSend", level: true, on: false },
];
const TO_MATRIX = [
    { kind: "matrix", level: true, on: false },
];
/**
 * Per type: MIDI addressing and which parameters the MixRack answers for.
 * The capabilities are what a DM0 actually replies to; the console also
 * answers some queries that make no sense (a mute group "fader", PEQ of a
 * UFX return under the FX return's address), so replies are only accepted
 * for parameters listed here.
 */
export const DLIVE_CHANNEL_TYPE_DEFS = {
    input: {
        ...STRIP,
        label: "Input",
        short: "Ip",
        midiOffset: 0,
        noteStart: 0x00,
        count: 128,
        mainAssign: true,
        hpf: true,
        peq: true,
        sends: INPUT_SENDS,
    },
    monoGroup: {
        ...STRIP,
        label: "Mono Group",
        short: "Grp",
        midiOffset: 1,
        noteStart: 0x00,
        count: 62,
        mainAssign: true,
        peq: true,
        sends: TO_MATRIX,
    },
    stereoGroup: {
        ...STRIP,
        label: "Stereo Group",
        short: "StGrp",
        midiOffset: 1,
        noteStart: 0x40,
        count: 31,
        stereo: true,
        mainAssign: true,
        peq: true,
        sends: TO_MATRIX,
    },
    monoAux: {
        ...STRIP,
        label: "Mono Aux",
        short: "Aux",
        midiOffset: 2,
        noteStart: 0x00,
        count: 62,
        peq: true,
        sends: TO_MATRIX,
    },
    stereoAux: {
        ...STRIP,
        label: "Stereo Aux",
        short: "StAux",
        midiOffset: 2,
        noteStart: 0x40,
        count: 31,
        stereo: true,
        peq: true,
        sends: TO_MATRIX,
    },
    monoMatrix: {
        ...STRIP,
        label: "Mono Matrix",
        short: "Mtx",
        midiOffset: 3,
        noteStart: 0x00,
        count: 62,
        peq: true,
    },
    stereoMatrix: {
        ...STRIP,
        label: "Stereo Matrix",
        short: "StMtx",
        midiOffset: 3,
        noteStart: 0x40,
        count: 31,
        stereo: true,
        peq: true,
    },
    monoFxSend: {
        ...STRIP,
        label: "Mono FX Send",
        short: "FX",
        midiOffset: 4,
        noteStart: 0x00,
        count: 16,
    },
    stereoFxSend: {
        ...STRIP,
        label: "Stereo FX Send",
        short: "StFX",
        midiOffset: 4,
        noteStart: 0x10,
        count: 16,
        stereo: true,
    },
    fxReturn: {
        ...STRIP,
        label: "FX Return",
        short: "FXRet",
        midiOffset: 4,
        noteStart: 0x20,
        count: 16,
        stereo: true,
        mainAssign: true,
        peq: true,
        sends: FX_RETURN_SENDS,
    },
    main: {
        ...STRIP,
        label: "Main",
        short: "Main",
        midiOffset: 4,
        noteStart: 0x30,
        count: 6,
        dcaAssign: false,
        sends: TO_MATRIX,
    },
    dca: {
        ...STRIP,
        label: "DCA",
        short: "DCA",
        midiOffset: 4,
        noteStart: 0x36,
        count: 24,
        dcaAssign: false,
    },
    muteGroup: {
        ...STRIP,
        label: "Mute Group",
        short: "MG",
        midiOffset: 4,
        noteStart: 0x4e,
        count: 8,
        name: false,
        colour: false,
        fader: false,
        dcaAssign: false,
    },
    ufxSend: {
        ...STRIP,
        label: "UFX Send",
        short: "UFX",
        midiOffset: 4,
        noteStart: 0x56,
        count: 8,
        stereo: true,
    },
    ufxReturn: {
        ...STRIP,
        label: "UFX Return",
        short: "UFXRet",
        midiOffset: 4,
        noteStart: 0x5e,
        count: 8,
        stereo: true,
        mainAssign: true,
        sends: FX_RETURN_SENDS,
    },
};
/** Channel types that make up each send destination kind */
export const DLIVE_SEND_KIND_TYPES = {
    group: ["monoGroup", "stereoGroup"],
    aux: ["monoAux", "stereoAux"],
    fxSend: ["monoFxSend", "stereoFxSend"],
    ufxSend: ["ufxSend"],
    matrix: ["monoMatrix", "stereoMatrix"],
};
export function dliveSendKindOf(type) {
    for (const [kind, types] of Object.entries(DLIVE_SEND_KIND_TYPES)) {
        if (types.includes(type))
            return kind;
    }
    return null;
}
/** The send capability from `source` to `dest`, or null when there is none */
export function dliveSendCapability(source, dest) {
    const kind = dliveSendKindOf(dest);
    if (!kind)
        return null;
    return (DLIVE_CHANNEL_TYPE_DEFS[source].sends.find((s) => s.kind === kind) ?? null);
}
export function isDLiveChannelType(value) {
    return DLIVE_CHANNEL_TYPES.includes(value);
}
/** "Ip 12", "StAux 3" (index is 0-based) */
export function dliveChannelLabel(type, index) {
    return `${DLIVE_CHANNEL_TYPE_DEFS[type].short} ${index + 1}`;
}
// ─── Colours ─────────────────────────────────────────────────
export const DLIVE_COLOURS = [
    "off",
    "red",
    "green",
    "yellow",
    "blue",
    "purple",
    "lightBlue",
    "white",
];
// ─── Value scales ────────────────────────────────────────────
/** Fader / send level at MIDI value 0, which the console treats as -inf */
export const DLIVE_FADER_MIN_DB = -54;
export const DLIVE_FADER_MAX_DB = 10;
// `+ 0` turns -0 into 0
const round1 = (n) => Math.round(n * 10) / 10 + 0;
const clamp7 = (n) => Math.max(0, Math.min(127, Math.round(n)));
/*
 * The spec converts with INT(), i.e. rounds down: a MIDI value v stands for
 * every unit value in [x(v), x(v + 1)). Encoders floor (with a little slack
 * for float error); decoders show the roundest number in that interval
 * (0x47 is "1000 Hz", not 951 Hz), so every MIDI value decodes and encodes
 * to itself.
 */
const floor7 = (n) => Math.max(0, Math.min(127, Math.floor(n + 1e-6)));
/** The roundest multiple of one of `steps` (coarse to fine) in [lower, upper) */
function roundest(lower, upper, steps) {
    for (const step of steps) {
        const candidate = Math.ceil(lower / step - 1e-6) * step;
        if (candidate < upper - 1e-6)
            return Math.round(candidate * 10) / 10 + 0;
    }
    return Math.round(lower * 10) / 10 + 0;
}
const DB_STEPS = [10, 5, 1, 0.5, 0.1];
const HZ_STEPS = [10000, 5000, 1000, 500, 100, 50, 10, 5, 1, 0.1];
/**
 * Fader and send levels: LV = INT((dB + 54) / 64 * 127), 0 = -inf.
 * 0 dB = 0x6B, +10 dB = 0x7F. Shown in 0.5 dB steps (one LV is 0.504 dB).
 */
export function dliveLevelToDb(lv) {
    if (lv <= 0)
        return DLIVE_FADER_MIN_DB;
    const db = (v) => (v * 64) / 127 + DLIVE_FADER_MIN_DB;
    return roundest(db(lv), db(lv + 1), DB_STEPS);
}
export function dliveDbToLevel(db) {
    if (db <= DLIVE_FADER_MIN_DB)
        return 0;
    return floor7(((db - DLIVE_FADER_MIN_DB) * 127) / 64);
}
export const DLIVE_PREAMP_MIN_DB = 5;
export const DLIVE_PREAMP_MAX_DB = 60;
const PREAMP_SPAN = DLIVE_PREAMP_MAX_DB - DLIVE_PREAMP_MIN_DB;
/** Preamp gain: GV = INT((dB - 5) / 55 * 127), +5 to +60 dB, 0.1 dB steps */
export function dliveGainToDb(gv) {
    const db = (v) => DLIVE_PREAMP_MIN_DB + (v * PREAMP_SPAN) / 127;
    return roundest(db(gv), db(gv + 1), DB_STEPS);
}
export function dliveDbToGain(db) {
    return floor7(((db - DLIVE_PREAMP_MIN_DB) * 127) / PREAMP_SPAN);
}
/**
 * A stereo input's image mode (Director: Preamp / Stereo Image / Mode), in
 * the console's order (AHNet value = index)
 */
export const DLIVE_STEREO_MODES = [
    "lr",
    "rl",
    "lPolR",
    "rPolL",
    "mono",
    "ll",
    "rr",
    "ms",
];
export const DLIVE_STEREO_MODE_LABELS = {
    lr: "L / R",
    rl: "R / L",
    lPolR: "L -Pol / R",
    rPolL: "R -Pol / L",
    mono: "Mono",
    ll: "L / L",
    rr: "R / R",
    ms: "M / S",
};
export const DLIVE_TRIM_MIN_DB = -24;
export const DLIVE_TRIM_MAX_DB = 24;
/**
 * Input trim over AHNet: uint16, 0x8000 + dB x 256 (Director: 0x87e0 =
 * +7.9 dB, 0x7d30 = -2.8 dB, 0x8000 = 0 dB). Director moves it in 1/16 dB
 * steps; so does the bridge.
 */
export function dliveTrimToValue(db) {
    const clamped = Math.min(DLIVE_TRIM_MAX_DB, Math.max(DLIVE_TRIM_MIN_DB, db));
    return 0x8000 + Math.round(clamped * 16) * 16;
}
export function dliveValueToTrim(value) {
    return (value - 0x8000) / 256;
}
/*
 * Frequencies are log scales over 0-127: PEQ 20 Hz-20 kHz, HPF 20 Hz-2 kHz,
 * f = min * (max / min) ^ (v / 127). The spec's formula (4608 * log2(f / 4)
 * ...) does not match firmware V2.01: checked against dLive Director, which
 * shows 63 Hz / 200 Hz / 2.5 kHz / 10 kHz for PEQ values 21 / 42 / 88 / 114
 * and HPF 98.6 / 100 / 147 Hz for 43 / 44 / 55.
 */
export const DLIVE_PEQ_MIN_HZ = 20;
export const DLIVE_PEQ_MAX_HZ = 20000;
export const DLIVE_HPF_MIN_HZ = 20;
export const DLIVE_HPF_MAX_HZ = 2000;
function freqToValue(hz, min, max) {
    return floor7((127 * Math.log(hz / min)) / Math.log(max / min));
}
/** ISO 1/3 octave centres, the values engineers (and presets) use */
const ISO_THIRD_OCTAVES = [
    20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630,
    800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000, 10000, 12500,
    16000, 20000,
];
/** An ISO 1/3 octave frequency in the value's range, else the roundest number */
function valueToFreq(v, min, max) {
    const hz = (x) => min * Math.pow(max / min, x / 127);
    const lower = hz(v) - 1e-6;
    const upper = hz(v + 1) - 1e-6;
    const iso = ISO_THIRD_OCTAVES.find((f) => f >= lower && f < upper);
    return iso ?? roundest(hz(v), hz(v + 1), HZ_STEPS);
}
export const dlivePeqFreqToValue = (hz) => freqToValue(hz, DLIVE_PEQ_MIN_HZ, DLIVE_PEQ_MAX_HZ);
export const dliveValueToPeqFreq = (v) => valueToFreq(v, DLIVE_PEQ_MIN_HZ, DLIVE_PEQ_MAX_HZ);
export const dliveHpfFreqToValue = (hz) => freqToValue(hz, DLIVE_HPF_MIN_HZ, DLIVE_HPF_MAX_HZ);
export const dliveValueToHpfFreq = (v) => valueToFreq(v, DLIVE_HPF_MIN_HZ, DLIVE_HPF_MAX_HZ);
export const DLIVE_PEQ_MIN_GAIN = -15;
export const DLIVE_PEQ_MAX_GAIN = 15;
/**
 * PEQ gain. The spec gives vv = (dB + 15) * 126 / 30 (0 dB = 0x3F), but every
 * flat band on the console reads 0x40, so 0x40 is 0 dB: vv = 64 + dB * 63 / 15
 * (-15 dB = 0x01, +15 dB = 0x7F).
 */
export function dlivePeqGainToValue(db) {
    return clamp7(64 + (db * 63) / 15);
}
export function dliveValueToPeqGain(v) {
    const db = ((v - 64) * 15) / 63;
    return round1(Math.max(DLIVE_PEQ_MIN_GAIN, db));
}
/** PEQ width (bandwidth) by MIDI value 0-24 */
export const DLIVE_PEQ_WIDTHS = [
    1.5, 1.4, 1.3, 1.2, 1.1, 1, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.667, 0.6, 0.55,
    0.5, 0.45, 0.4, 0.333, 0.3, 0.25, 0.2, 0.167, 0.13, 0.111,
];
export function dliveValueToPeqWidth(v) {
    return DLIVE_PEQ_WIDTHS[Math.max(0, Math.min(DLIVE_PEQ_WIDTHS.length - 1, v))];
}
/** The MIDI value of the table width closest to `width` */
export function dlivePeqWidthToValue(width) {
    let best = 0;
    for (let i = 1; i < DLIVE_PEQ_WIDTHS.length; i++) {
        if (Math.abs(DLIVE_PEQ_WIDTHS[i] - width) <
            Math.abs(DLIVE_PEQ_WIDTHS[best] - width))
            best = i;
    }
    return best;
}
export const DLIVE_PEQ_TYPES = [
    "bell",
    "lowShelf",
    "highShelf",
    "lowPass",
    "highPass",
];
/**
 * Filter types each PEQ band accepts. Bands 1 and 2 are always bells; band 0
 * can be a low shelf or high pass, band 3 a high shelf or low pass.
 */
export const DLIVE_PEQ_BAND_TYPES = [
    ["bell", "lowShelf", "highPass"],
    ["bell"],
    ["bell"],
    ["bell", "highShelf", "lowPass"],
];
export const DLIVE_PEQ_BANDS = 4;
// ─── Preamps (MixRack / DX sockets) ──────────────────────────
export const DLIVE_PREAMP_BANKS = ["local", "dx12", "dx34"];
export const DLIVE_PREAMP_BANK_DEFS = {
    local: { label: "MixRack", start: 0x00, count: 64 },
    dx12: { label: "DX 1/2", start: 0x40, count: 32 },
    dx34: { label: "DX 3/4", start: 0x60, count: 32 },
};
/** Socket (MP) number of a preamp, 0x00-0x7F */
export function dlivePreampSocket(bank, index) {
    return DLIVE_PREAMP_BANK_DEFS[bank].start + index;
}
export function dliveSocketToPreamp(mp) {
    for (const bank of DLIVE_PREAMP_BANKS) {
        const def = DLIVE_PREAMP_BANK_DEFS[bank];
        if (mp >= def.start && mp < def.start + def.count) {
            return { bank, index: mp - def.start };
        }
    }
    return null;
}
// ─── UFX ─────────────────────────────────────────────────────
export const DLIVE_UFX_KEYS = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
];
export const DLIVE_UFX_SCALES = ["major", "minor"];
// ─── Scenes ──────────────────────────────────────────────────
export const DLIVE_SCENE_COUNT = 500;
export const DLIVE_SOURCE_GROUPS = {
    local: {
        label: "MixRack",
        type: 0x00,
        offset: 0,
        count: 64,
        preampBank: "local",
    },
    dx12: {
        label: "DX 1/2",
        type: 0x05,
        offset: 0,
        count: 64,
        preampBank: "dx12",
    },
    dx34: {
        label: "DX 3/4",
        type: 0x06,
        offset: 0,
        count: 64,
        preampBank: "dx34",
    },
    port1: {
        label: "I/O Port 1",
        type: 0x02,
        offset: 0,
        count: 128,
        ioPort: 1,
    },
    port2: {
        label: "I/O Port 2",
        type: 0x03,
        offset: 0,
        count: 128,
        ioPort: 2,
    },
    port3: {
        label: "I/O Port 3",
        type: 0x04,
        offset: 0,
        count: 128,
        ioPort: 3,
    },
    surface: { label: "Surface", type: 0x01, offset: 0, count: 12 },
    surfaceDx: { label: "Surface DX 5/6", type: 0x01, offset: 12, count: 32 },
    surfacePort4: {
        label: "Surface I/O Port 4",
        type: 0x01,
        offset: 44,
        count: 128,
        ioPort: 4,
    },
    surfacePort5: {
        label: "Surface I/O Port 5",
        type: 0x01,
        offset: 172,
        count: 128,
        ioPort: 5,
    },
    surfaceUsb: { label: "Surface USB", type: 0x01, offset: 300, count: 2 },
    monoGroup: {
        label: "Mono Group",
        type: 0x0c,
        offset: 0,
        count: 62,
        internal: true,
        unit: "Group",
    },
    stereoGroup: {
        label: "Stereo Group",
        type: 0x0d,
        offset: 0,
        count: 31,
        stereo: true,
        internal: true,
        unit: "Group",
    },
    monoAux: {
        label: "Mono Aux",
        type: 0x0e,
        offset: 0,
        count: 62,
        internal: true,
        unit: "Aux",
    },
    stereoAux: {
        label: "Stereo Aux",
        type: 0x0f,
        offset: 0,
        count: 31,
        stereo: true,
        internal: true,
        unit: "Aux",
    },
    main: {
        label: "Main",
        type: 0x10,
        offset: 0,
        count: 6,
        stereo: true,
        internal: true,
        unit: "Main",
    },
    monoMatrix: {
        label: "Mono Matrix",
        type: 0x11,
        offset: 0,
        count: 62,
        internal: true,
        unit: "Matrix",
    },
    stereoMatrix: {
        label: "Stereo Matrix",
        type: 0x12,
        offset: 0,
        count: 31,
        stereo: true,
        internal: true,
        unit: "Matrix",
    },
    pafl: {
        label: "PAFL",
        type: 0x13,
        offset: 0,
        count: 2,
        stereo: true,
        internal: true,
        unit: "PAFL",
    },
    rackFx: {
        label: "Rack FX out",
        type: 0x1c,
        offset: 0,
        count: 64,
        internal: true,
        unit: "Output",
    },
    ultraFx: {
        label: "UltraFX out",
        type: 0x2d,
        offset: 0,
        count: 32,
        internal: true,
        unit: "Output",
    },
    sigGen: {
        label: "SigGen",
        type: 0x2a,
        offset: 0,
        count: 1,
        internal: true,
        unit: "SigGen",
    },
};
const isStereo = (group) => !!DLIVE_SOURCE_GROUPS[group].stereo;
export const DLIVE_SOURCE_GROUP_IDS = Object.keys(DLIVE_SOURCE_GROUPS);
export const DLIVE_SOURCE_UNPATCHED = 0x14;
/** "port2:63", "stereoGroup:5L", "none" or "raw:2b0000" */
export function formatDLiveSource(source) {
    if (source.group === "none")
        return "none";
    if (source.group === "raw")
        return `raw:${source.entry}`;
    return `${source.group}:${source.socket}${isStereo(source.group) ? (source.side ?? "L") : ""}`;
}
export function parseDLiveSource(value) {
    if (value === "none")
        return { group: "none" };
    const raw = value.match(/^raw:([0-9a-f]{6})$/);
    if (raw)
        return { group: "raw", entry: raw[1] };
    const m = value.match(/^([a-zA-Z0-9]+):(\d+)([LR])?$/);
    if (!m || !(m[1] in DLIVE_SOURCE_GROUPS))
        return null;
    const group = m[1];
    const socket = Number(m[2]);
    if (socket < 1 || socket > DLIVE_SOURCE_GROUPS[group].count)
        return null;
    if (isStereo(group)) {
        if (!m[3])
            return null;
        return { group, socket, side: m[3] };
    }
    if (m[3])
        return null;
    return { group, socket };
}
/** A 3-byte table entry to a source */
export function decodeDLiveSourceEntry(entry) {
    const [type, hi, lo] = entry;
    if (type === DLIVE_SOURCE_UNPATCHED)
        return { group: "none" };
    const index = (hi << 8) | lo;
    // Groups of one type are ordered by offset; take the last that starts at or before
    let match = null;
    for (const id of DLIVE_SOURCE_GROUP_IDS) {
        const def = DLIVE_SOURCE_GROUPS[id];
        if (def.type === type && index >= def.offset) {
            if (!match || def.offset > DLIVE_SOURCE_GROUPS[match].offset)
                match = id;
        }
    }
    if (match) {
        const n = index - DLIVE_SOURCE_GROUPS[match].offset;
        if (isStereo(match)) {
            const socket = Math.floor(n / 2) + 1;
            if (socket <= DLIVE_SOURCE_GROUPS[match].count) {
                return { group: match, socket, side: n % 2 ? "R" : "L" };
            }
        }
        else if (n < DLIVE_SOURCE_GROUPS[match].count) {
            return { group: match, socket: n + 1 };
        }
    }
    const hex = [type, hi, lo]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return { group: "raw", entry: hex };
}
export function encodeDLiveSourceEntry(source) {
    if (source.group === "none")
        return Uint8Array.of(DLIVE_SOURCE_UNPATCHED, 0, 0);
    if (source.group === "raw") {
        return Uint8Array.from(source.entry.match(/../g).map((h) => parseInt(h, 16)));
    }
    const def = DLIVE_SOURCE_GROUPS[source.group];
    const n = isStereo(source.group)
        ? (source.socket - 1) * 2 + (source.side === "R" ? 1 : 0)
        : source.socket - 1;
    const index = def.offset + n;
    return Uint8Array.of(def.type, (index >> 8) & 0xff, index & 0xff);
}
/** "I/O Port 2 (MADI)", "MixRack" */
export function dliveSourceGroupLabel(group, names) {
    const def = DLIVE_SOURCE_GROUPS[group];
    const name = def.ioPort ? names?.[def.ioPort] : undefined;
    return name ? `${def.label} (${name})` : def.label;
}
/** "I/O Port 2 (MADI) 63", "Unpatched" */
export function dliveSourceLabel(value, names) {
    if (!value)
        return "–";
    const source = parseDLiveSource(value);
    if (!source)
        return value;
    if (source.group === "none")
        return "Unpatched";
    if (source.group === "raw")
        return `Other (${source.entry})`;
    return `${dliveSourceGroupLabel(source.group, names)} ${source.socket}${source.side ? ` ${source.side}` : ""}`;
}
/**
 * The source a stereo pair's right input gets when the left one is patched
 * to `value`: the next socket ("port2:63" -> "port2:64"), the R side of a
 * stereo bus, nothing for nothing. Null when there is no next socket.
 */
export function dliveStereoPartnerSource(value) {
    const source = parseDLiveSource(value);
    if (!source || source.group === "raw")
        return null;
    if (source.group === "none")
        return "none";
    if (source.side) {
        return formatDLiveSource({
            group: source.group,
            socket: source.socket,
            side: "R",
        });
    }
    if (source.socket >= DLIVE_SOURCE_GROUPS[source.group].count)
        return null;
    return formatDLiveSource({ group: source.group, socket: source.socket + 1 });
}
/**
 * A stereo pair's sources in one label: "I/O Port 2 63/64" when the right
 * input takes the next socket, otherwise both ("I/O Port 2 63 / MixRack 5")
 */
export function dliveStereoSourceLabel(left, right, names) {
    if (!left || !right)
        return dliveSourceLabel(left ?? right, names);
    const source = parseDLiveSource(left);
    if (source &&
        source.group !== "none" &&
        source.group !== "raw" &&
        dliveStereoPartnerSource(left) === right) {
        const group = dliveSourceGroupLabel(source.group, names);
        return source.side
            ? `${group} ${source.socket}`
            : `${group} ${source.socket}/${source.socket + 1}`;
    }
    if (left === "none" && right === "none")
        return "Unpatched";
    return `${dliveSourceLabel(left, names)} / ${dliveSourceLabel(right, names)}`;
}
/** The MIDI preamp (bank, 0-based index) behind a source, if there is one */
export function dlivePreampOfSource(value) {
    const source = value ? parseDLiveSource(value) : null;
    if (!source || source.group === "none" || source.group === "raw")
        return null;
    const bank = DLIVE_SOURCE_GROUPS[source.group].preampBank;
    if (!bank)
        return null;
    const index = source.socket - 1;
    return index < DLIVE_PREAMP_BANK_DEFS[bank].count ? { bank, index } : null;
}
// ─── Meters (over AHNet) ─────────────────────────────────────
/**
 * Input meter points, in signal order. Words 0-1 are before the PEQ and 2-4
 * after it (checked with a PEQ boost); the names follow the console's meter
 * source points.
 */
export const DLIVE_INPUT_METER_POINTS = [
    "input",
    "postPreamp",
    "postGatePeq",
    "postCompressor",
    "postDelay",
];
/** dBFS; the console's meters read 0 at -18 dBFS */
export const DLIVE_METER_HEADROOM_DB = 18;
